// frontend/src/components/FinanceReport/Accounting/components/accounts/clients/ClientsTab.jsx

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAllAccountingRecords, getAllClients } from "./requests";

const formatNumber = (v) =>
  Number(v || 0).toLocaleString("ru-RU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export default function ClientsTab() {
  const navigate = useNavigate();

  const [records, setRecords] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [recordsList, clientsList] = await Promise.all([
        getAllAccountingRecords(),
        getAllClients().catch(() => []),
      ]);
      setRecords(recordsList);
      setClients(clientsList);
    } catch (err) {
      setError(err.message || "Ошибка загрузки");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Группировка платежей по клиенту:
  // unallocated = сумма unallocated_sum по всем платежам клиента
  const clientRows = useMemo(() => {
    const byId = new Map();

    // Сначала все клиенты из справочника
    clients.forEach((c) => {
      byId.set(String(c.id), {
        id: c.id,
        name: c.name || `Клиент #${c.id}`,
        unallocated: 0,
        paymentsCount: 0,
      });
    });

    // Потом добавляем тех, кто есть в платежах, но нет в справочнике
    records.forEach((r) => {
      if (r.client == null) return;
      const key = String(r.client);
      if (!byId.has(key)) {
        byId.set(key, {
          id: r.client,
          name: r.client_name || `Клиент #${r.client}`,
          unallocated: 0,
          paymentsCount: 0,
        });
      }
      const row = byId.get(key);
      row.unallocated += Number(r.unallocated_sum || 0);
      row.paymentsCount += 1;
    });

    // Сортируем: сначала те, у кого больше нераспределённого
    return Array.from(byId.values()).sort(
      (a, b) => b.unallocated - a.unallocated
    );
  }, [clients, records]);

  const totalUnallocated = useMemo(
    () => clientRows.reduce((sum, c) => sum + c.unallocated, 0),
    [clientRows]
  );

  if (loading) return <div className="p-4 text-slate-500">Загрузка…</div>;

  return (
    <>
      <div className="mb-4">
        <h2 className="text-2xl font-bold">Счета клиентов</h2>
        <p className="mt-1 text-sm text-slate-500">
          Нажмите на клиента, чтобы открыть движение по его платежам.
        </p>
      </div>

      {error && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="p-4 border rounded-xl mb-6 bg-gray-50">
        <p className="text-sm text-gray-500">
          Сумма нераспределённых платежей
        </p>
        <p className="text-2xl font-bold text-black">
          ¥ {formatNumber(totalUnallocated)}
        </p>
      </div>

      <div className="space-y-4">
        {clientRows.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
            <div className="text-3xl">👥</div>
            <p className="mt-2 font-semibold text-slate-800">Клиентов пока нет</p>
            <p className="mt-1 text-sm text-slate-500">
              Клиент появится здесь после первого платежа.
            </p>
          </div>
        ) : (
          clientRows.map((c) => (
            <div
              key={c.id}
              onClick={() => navigate(`/accounting/client-accounts/${c.id}`)}
              className="flex justify-between items-start p-4 rounded-xl border bg-white cursor-pointer transition hover:shadow-sm"
            >
              <div>
                <p className="font-semibold text-lg">{c.name}</p>
                <p
                  className={`text-lg font-medium ${
                    c.unallocated > 0 ? "text-amber-700" : "text-slate-500"
                  }`}
                >
                  Не распределено: ¥ {formatNumber(c.unallocated)}
                </p>
                <p className="text-sm text-gray-400">
                  Платежей: {c.paymentsCount}
                </p>
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );
}